import { parseDateOnly } from './date.ts';
import type { CalendarMonth } from './types';

const MONTHS_PER_YEAR = 12;
export const YEAR_PAGE_SIZE = 12;
// Opens the year grid with the shown year on the second row, leaving more room ahead.
const YEARS_BEFORE_SHOWN = 4;

/** Inclusive month-index limits (year * 12 + month); null means unbounded. */
export interface MonthBounds {
    min: number | null;
    max: number | null;
}

export const monthIndex = (m: CalendarMonth): number => m.year * MONTHS_PER_YEAR + m.month;

export function resolveMonthBounds(
    minMonth?: CalendarMonth,
    maxMonth?: CalendarMonth,
    minDate?: string
): MonthBounds {
    const floor = minDate ? parseDateOnly(minDate) : null;
    const lows = [
        ...(minMonth ? [monthIndex(minMonth)] : []),
        ...(floor ? [monthIndex({ year: floor.getFullYear(), month: floor.getMonth() })] : [])
    ];
    return {
        min: lows.length ? Math.max(...lows) : null,
        max: maxMonth ? monthIndex(maxMonth) : null
    };
}

export function isMonthOutOfBounds(index: number, bounds: MonthBounds): boolean {
    return (
        (bounds.min !== null && index < bounds.min) || (bounds.max !== null && index > bounds.max)
    );
}

export function isYearOutOfBounds(year: number, bounds: MonthBounds): boolean {
    const first = year * MONTHS_PER_YEAR;
    const last = first + MONTHS_PER_YEAR - 1;
    return (
        (bounds.min !== null && last < bounds.min) || (bounds.max !== null && first > bounds.max)
    );
}

export function clampMonth(month: CalendarMonth, bounds: MonthBounds): CalendarMonth {
    const index = monthIndex(month);
    const clamped = Math.min(Math.max(index, bounds.min ?? -Infinity), bounds.max ?? Infinity);
    return { year: Math.floor(clamped / MONTHS_PER_YEAR), month: clamped % MONTHS_PER_YEAR };
}

export function yearPageStart(year: number): number {
    return year - YEARS_BEFORE_SHOWN;
}
